// What we charge, in each currency we charge in. (#826)
//
// These must match Stripe exactly: the Prices behind our Payment Links carry USD as
// the default and CAD as a `currency_options` amount, and Payment Links pick the
// currency from the customer's IP. A price on this site that differs from the one
// Stripe then charges is the kind of small untruth we do not do, so change these only
// alongside the Stripe Prices (live below; the sandbox Prices dev uses need the same
// CAD options, or dev shows CA$ and charges USD):
//
//   Monthly  price_1TRaJ07kofcvigZQSz0gafUL   USD 9.99    CAD 12.99
//   Annual   price_1TRaL67kofcvigZQCyEmshK6   USD 99.99   CAD 129.00

export type Currency = 'usd' | 'cad';
export type PaidPlan = 'monthly' | 'annual';

export const PRICES: Record<Currency, Record<PaidPlan, number>> = {
  usd: { monthly: 9.99, annual: 99.99 },
  cad: { monthly: 12.99, annual: 129.0 },
};

/**
 * Every time zone a browser in Canada can report. Both the canonical IANA names and
 * the older ones browsers actually return: Chrome's ICU reports America/Montreal as
 * America/Toronto, America/Yellowknife as America/Edmonton, America/Atikokan as
 * America/Coral_Harbour. Browsers that report zones as the system names them
 * (Firefox, Safari) can also return the old Canada/* aliases. prices.test.ts checks
 * the list against what the runtime reports, so a zone that resolves to a name
 * missing here fails there, not in a customer's browser.
 */
const CANADA_TIMEZONES = new Set([
  'America/St_Johns', 'America/Halifax', 'America/Glace_Bay', 'America/Moncton',
  'America/Goose_Bay', 'America/Blanc-Sablon', 'America/Toronto', 'America/Montreal',
  'America/Nipigon', 'America/Thunder_Bay', 'America/Atikokan', 'America/Coral_Harbour',
  'America/Iqaluit', 'America/Pangnirtung', 'America/Winnipeg', 'America/Rainy_River',
  'America/Resolute', 'America/Rankin_Inlet', 'America/Regina', 'America/Swift_Current',
  'America/Edmonton', 'America/Cambridge_Bay', 'America/Yellowknife', 'America/Inuvik',
  'America/Creston', 'America/Dawson_Creek', 'America/Fort_Nelson', 'America/Vancouver',
  'America/Whitehorse', 'America/Dawson',
  'Canada/Atlantic', 'Canada/Central', 'Canada/Eastern', 'Canada/Mountain',
  'Canada/Newfoundland', 'Canada/Pacific', 'Canada/Saskatchewan', 'Canada/Yukon',
]);

/**
 * The currency to show this visitor, from their time zone.
 *
 * Only a guess at what Stripe will charge, which it decides from the IP address at
 * checkout, so the pricing page says so. The two agree for almost everyone; they can
 * differ for someone travelling or on a VPN. Where the currency is known for certain,
 * a subscriber's own plan, use the currency Stripe recorded instead.
 */
export function visitorCurrency(): Currency {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && CANADA_TIMEZONES.has(tz) ? 'cad' : 'usd';
  } catch {
    return 'usd';
  }
}

/** A currency the server recorded, normalised; anything unrecognised is USD. */
export function recordedCurrency(value: unknown): Currency {
  return String(value || '').toLowerCase() === 'cad' ? 'cad' : 'usd';
}

/**
 * "$9.99", "CA$12.99", "CA$129". The CA prefix always, since a bare "$" means the US
 * dollar to most visitors. Whole amounts drop the cents, as on the pricing cards.
 */
export function formatMoney(amount: number, currency: Currency): string {
  const whole = Math.round(amount * 100) % 100 === 0;
  const digits = whole ? 0 : 2;
  const n = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(amount);
  return `${currency === 'cad' ? 'CA$' : '$'}${n}`;
}

export const planPrice = (plan: PaidPlan, currency: Currency): string =>
  formatMoney(PRICES[currency][plan], currency);

/** The annual plan's cost per month, for the "$8.33 / month" line. */
export const annualPerMonth = (currency: Currency): string =>
  formatMoney(Math.round((PRICES[currency].annual / 12) * 100) / 100, currency);

/**
 * Months saved by paying annually, rounded: the "2 months free" line. Computed so it
 * cannot quietly become untrue when a price changes. prices.test.ts pins it at 2 for
 * today's prices in both currencies.
 */
export const monthsFree = (currency: Currency): number => {
  const p = PRICES[currency];
  return Math.round((p.monthly * 12 - p.annual) / p.monthly);
};
